import { useState } from 'react'

import {
  CalloutNotesSection,
  type CalloutNotesSectionNote,
} from '@/components/CalloutNotesSection'
import type {
  PhotoNoteReplace,
  PhotoNoteSubmit,
} from '@/components/PhotoNoteComposer'
import {
  FollowUpActionDialog,
  type FollowUpAction,
} from '@/components/FollowUpActionDialog'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { UnderlineInput } from '@/components/ui/underline-input'
import { LedgerBadge, type LedgerBadgeTone } from '@/components/ui/LedgerTable'
import { dueState, type ScheduleStep } from '@/db/domain/followUps'
import {
  dueLabel,
  type FollowUpLinkedNote,
  type FollowUpRow,
} from '@/screens/FollowUpsScreen'

/** A follow-up as a thread: the question in the sidebar, answers in the main pane. */
export interface FollowUpThread {
  id: string
  title: string
  details?: string
  status: 'open' | 'closed'
  dueAt: string
  createdAt: string
  schedule: ScheduleStep[]
  scheduleIndex: number
  /** Feed-ready thread contents, tombstones included. */
  feed: CalloutNotesSectionNote[]
  /** Live answers, newest first — gates done/close and fills the action dialog. */
  answers: FollowUpLinkedNote[]
  assigneeName?: string
  lastActivityAt: string
}

/**
 * Where a note action lands: the customer's general thread, a follow-up's
 * answers, or a service's own work notes. Handlers dispatch on this — the
 * conversation view itself never talks to a data layer.
 */
export type ThreadTarget =
  | { kind: 'general' }
  | { kind: 'followUp'; followUpId: string }

export interface CustomerConversationProps {
  customerId: string
  customerName: string
  followUpThreads: FollowUpThread[]
  generalNotes: CalloutNotesSectionNote[]
  newNoteAuthorName: string
  /**
   * Which thread is shown, as a stable key ('general', 'fu:<id>',
   * 'fu:<id>'). When set with `onSelectThread`, selection is
   * controlled — the container keeps it in the URL so threads deep-link.
   */
  selectedThreadKey?: string
  onSelectThread?: (key: string) => void
  onAddNote?: (text: string, target: ThreadTarget) => boolean
  onSubmitPhotoNote?: (input: PhotoNoteSubmit, target: ThreadTarget) => void
  onReplacePhotoNote?: (
    id: string,
    input: PhotoNoteReplace,
    target: ThreadTarget,
  ) => void
  onSaveEditNote?: (id: string, text: string, target: ThreadTarget) => void
  onDeleteNote?: (id: string, target: ThreadTarget) => void
  photoNoteError?: string | null
  onClearPhotoNoteError?: () => void
  onCompleteFollowUp?: (id: string, options: { note?: string }) => void
  onSnoozeFollowUp?: (
    id: string,
    step: ScheduleStep,
    options: { note?: string },
  ) => void
  /** A snooze to a picked date — moves the due date, leaves the ladder alone. */
  onRescheduleFollowUp?: (
    id: string,
    dueAt: string,
    options: { note?: string },
  ) => void
  onCloseFollowUp?: (id: string, options: { note?: string }) => void
  onReopenFollowUp?: (
    id: string,
    dueAt: string,
    options: { note?: string },
  ) => void
  /** Open the History tab — where resolved threads and archived services live. */
  onOpenHistory?: () => void
}

const dueTone: Record<ReturnType<typeof dueState>, LedgerBadgeTone> = {
  overdue: 'amber',
  today: 'blue',
  upcoming: 'slate',
}

type Selection = { kind: 'general' } | { kind: 'followUp'; id: string }

/** The stable, URL-safe key for a thread. */
export function threadKey(selection: Selection): string {
  return selection.kind === 'general' ? 'general' : `fu:${selection.id}`
}

function parseThreadKey(key: string): Selection {
  if (key.startsWith('fu:')) {
    return { kind: 'followUp', id: key.slice(3) }
  }
  // Older links may still carry svc:* keys — they land on the general thread.
  return { kind: 'general' }
}

const toolbarButton =
  'inline-flex h-8 items-center rounded-lg border border-slate-300 bg-white px-3 text-[12px] text-slate-600 hover:bg-slate-50'
const toolbarButtonDisabled =
  'inline-flex h-8 items-center rounded-lg border border-slate-200 bg-slate-100 px-3 text-[12px] text-slate-400'

/**
 * The customer conversation as threads: a sidebar of thread titles, a main
 * pane with the selected thread's notes.
 *
 * Every thread renders through the same notes section the service pages use,
 * so text notes, photo notes, edits and tombstones behave identically
 * everywhere — the only thing the selection changes is which notes show and
 * where a new one lands. General notes are simply the thread with no question
 * attached.
 */
export function CustomerConversation({
  customerId,
  customerName,
  followUpThreads,
  generalNotes,
  newNoteAuthorName,
  selectedThreadKey,
  onSelectThread,
  onAddNote,
  onSubmitPhotoNote,
  onReplacePhotoNote,
  onSaveEditNote,
  onDeleteNote,
  photoNoteError,
  onClearPhotoNoteError,
  onCompleteFollowUp,
  onSnoozeFollowUp,
  onRescheduleFollowUp,
  onCloseFollowUp,
  onReopenFollowUp,
  onOpenHistory,
}: CustomerConversationProps) {
  const [localThreadKey, setLocalThreadKey] = useState('general')
  const selection = parseThreadKey(selectedThreadKey ?? localThreadKey)
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null)
  const [pendingAction, setPendingAction] = useState<FollowUpAction | null>(
    null,
  )
  // The mobile thread selector: a dropdown with a text filter, in place of the
  // desktop sidebar.
  const [isPickerOpen, setIsPickerOpen] = useState(false)
  const [pickerFilter, setPickerFilter] = useState('')
  const now = new Date()

  const openThreads = followUpThreads
    .filter((thread) => thread.status === 'open')
    .sort((a, b) => a.dueAt.localeCompare(b.dueAt))
  const closedThreads = followUpThreads
    .filter((thread) => thread.status === 'closed')
    .sort((a, b) => b.lastActivityAt.localeCompare(a.lastActivityAt))

  // A thread that vanished underneath the selection (deleted, resynced)
  // degrades to the general thread rather than a blank pane.
  const selectedFollowUp =
    selection.kind === 'followUp'
      ? followUpThreads.find((thread) => thread.id === selection.id)
      : undefined
  const effective: Selection =
    selection.kind === 'followUp' && selectedFollowUp
      ? selection
      : { kind: 'general' }

  const select = (next: Selection) => {
    const key = threadKey(next)
    if (onSelectThread) {
      onSelectThread(key)
    } else {
      setLocalThreadKey(key)
    }
    setEditingNoteId(null)
  }

  /**
   * One flat list drives both pickers: the desktop sidebar renders it grouped
   * by section, the mobile thread selector renders it filtered by `label`.
   */
  interface ThreadEntry {
    key: string
    section: 'General' | 'Due now' | 'Upcoming' | 'Resolved'
    label: string
    content: React.ReactNode
  }

  const selectedKey = threadKey(effective)

  // Only live work makes the list: open follow-ups, split into what needs
  // acting on now versus what is still coming. The one exception is whatever
  // is being looked at right now — a resolved thread reached through History
  // or a deep link stays visible while selected.
  const dueNowThreads = openThreads.filter(
    (thread) => dueState(thread, now) !== 'upcoming',
  )
  const upcomingThreads = openThreads.filter(
    (thread) => dueState(thread, now) === 'upcoming',
  )
  const visibleClosedThreads = closedThreads.filter(
    (thread) => `fu:${thread.id}` === selectedKey,
  )

  const followUpEntry = (
    thread: FollowUpThread,
    section: ThreadEntry['section'],
  ): ThreadEntry => ({
    key: `fu:${thread.id}`,
    section,
    label: thread.title,
    content: (
      <>
        {thread.status === 'open' ? (
          <LedgerBadge tone={dueTone[dueState(thread, now)]}>
            {dueLabel(thread.dueAt, now)}
          </LedgerBadge>
        ) : (
          <span className="text-emerald-600" title="Resolved">
            ✓
          </span>
        )}
        <span className="min-w-0 flex-1 truncate text-[12.5px] text-slate-800">
          {thread.title}
        </span>
        <span className="text-[11px] text-slate-400">
          {thread.answers.length || ''}
        </span>
      </>
    ),
  })

  const threadEntries: ThreadEntry[] = [
    {
      key: 'general',
      section: 'General',
      label: 'General notes',
      content: (
        <>
          <span className="min-w-0 flex-1 truncate text-[12.5px] font-medium text-slate-800">
            General notes
          </span>
          <span className="text-[11px] text-slate-400">
            {generalNotes.length || ''}
          </span>
        </>
      ),
    },
    ...dueNowThreads.map((thread) => followUpEntry(thread, 'Due now')),
    ...upcomingThreads.map((thread) => followUpEntry(thread, 'Upcoming')),
    ...visibleClosedThreads.map((thread) => followUpEntry(thread, 'Resolved')),
  ]

  const selectedEntry =
    threadEntries.find((entry) => entry.key === selectedKey) ?? threadEntries[0]

  const entryButton = (entry: ThreadEntry, afterSelect?: () => void) => (
    <button
      key={entry.key}
      type="button"
      className={
        entry.key === selectedKey
          ? 'flex w-full items-center gap-1.5 rounded-md border border-violet-300 bg-violet-50 px-2 py-1.5 text-left'
          : 'flex w-full items-center gap-1.5 rounded-md border border-transparent px-2 py-1.5 text-left hover:bg-slate-100'
      }
      onClick={() => {
        select(parseThreadKey(entry.key))
        afterSelect?.()
      }}
    >
      {entry.content}
    </button>
  )

  const pickerQuery = pickerFilter.trim().toLowerCase()
  const pickerEntries = pickerQuery
    ? threadEntries.filter((entry) =>
        entry.label.toLowerCase().includes(pickerQuery),
      )
    : threadEntries

  const sectionsOf = (entries: ThreadEntry[]) =>
    (['General', 'Due now', 'Upcoming', 'Resolved'] as const)
      .map((section) => ({
        section,
        entries: entries.filter((entry) => entry.section === section),
      }))
      .filter((group) => group.entries.length > 0)

  const closePicker = () => {
    setIsPickerOpen(false)
    setPickerFilter('')
  }

  /** What the action dialog needs to know about the selected follow-up. */
  const toActionRow = (thread: FollowUpThread): FollowUpRow => ({
    id: thread.id,
    customerId,
    customerName,
    title: thread.title,
    details: thread.details,
    dueAt: thread.dueAt,
    schedule: thread.schedule,
    scheduleIndex: thread.scheduleIndex,
    status: thread.status,
    notes: thread.answers,
  })

  const mainFeed: CalloutNotesSectionNote[] =
    effective.kind === 'general'
      ? generalNotes
      : (selectedFollowUp?.feed ?? [])

  const target: ThreadTarget =
    effective.kind === 'general'
      ? { kind: 'general' }
      : { kind: 'followUp', followUpId: effective.id }
  const answered = (selectedFollowUp?.answers.length ?? 0) > 0

  return (
    <div className="gap-4 sm:grid sm:grid-cols-[14rem_1fr]">
      {/* Thread list: a sidebar on desktop, a filterable selector on mobile. */}
      <nav
        aria-label="Threads"
        className="hidden text-xs sm:flex sm:flex-col sm:gap-1 sm:border-r sm:border-slate-200 sm:pr-3"
      >
        {sectionsOf(threadEntries).map((group) => (
          <div key={group.section} className="flex flex-col gap-1">
            {group.section !== 'General' ? (
              <div
                className={
                  group.section === 'Due now'
                    ? 'mt-2 text-[10.5px] font-semibold uppercase tracking-wide text-violet-700'
                    : 'mt-2 text-[10.5px] font-semibold uppercase tracking-wide text-slate-500'
                }
              >
                {group.section}
              </div>
            ) : null}
            {group.entries.map((entry) => entryButton(entry))}
          </div>
        ))}
        {onOpenHistory ? (
          <button
            type="button"
            className="mt-3 cursor-pointer px-2 text-left text-[11px] text-blue-700 hover:underline"
            onClick={onOpenHistory}
          >
            Resolved &amp; archived → History
          </button>
        ) : null}
      </nav>

      {/* Mobile thread selector: current thread + a tap to switch. */}
      <div className="mb-3 sm:hidden">
        <button
          type="button"
          className="flex w-full items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-left text-xs"
          onClick={() => setIsPickerOpen(true)}
        >
          {selectedEntry.content}
          <span aria-hidden="true" className="text-slate-400">
            ▾
          </span>
        </button>
      </div>

      {isPickerOpen ? (
        <Dialog
          open
          onOpenChange={(open) => (open ? undefined : closePicker())}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Threads</DialogTitle>
            </DialogHeader>
            <UnderlineInput
              value={pickerFilter}
              autoFocus
              placeholder="Filter threads…"
              aria-label="Filter threads"
              onChange={(event) => setPickerFilter(event.target.value)}
            />
            <div className="max-h-[55vh] space-y-1 overflow-y-auto text-xs">
              {pickerEntries.length === 0 ? (
                <p className="py-2 text-[12px] text-slate-500">
                  No threads match “{pickerFilter.trim()}”.
                </p>
              ) : (
                sectionsOf(pickerEntries).map((group) => (
                  <div key={group.section} className="space-y-1">
                    {group.section !== 'General' ? (
                      <div className="mt-2 text-[10.5px] font-semibold uppercase tracking-wide text-slate-500">
                        {group.section}
                      </div>
                    ) : null}
                    {group.entries.map((entry) =>
                      entryButton(entry, closePicker),
                    )}
                  </div>
                ))
              )}
            </div>
            {onOpenHistory ? (
              <button
                type="button"
                className="cursor-pointer text-left text-[11px] text-blue-700 hover:underline"
                onClick={() => {
                  closePicker()
                  onOpenHistory()
                }}
              >
                Resolved &amp; archived → History
              </button>
            ) : null}
          </DialogContent>
        </Dialog>
      ) : null}

      {/* Main pane: toolbar + the selected thread's notes. */}
      <div className="min-w-0 space-y-3">
        <div
          className={
            // On a phone the thread selector above already names the thread, so
            // a general-notes toolbar (title only, no actions) is pure noise.
            effective.kind === 'general'
              ? 'flex flex-wrap items-center gap-2 border-b border-slate-200 pb-2 max-sm:hidden'
              : 'flex flex-wrap items-center gap-2 border-b border-slate-200 pb-2'
          }
        >
          {effective.kind === 'general' ? (
            <span className="min-w-0 flex-1 text-[13px] font-medium text-slate-900">
              General notes
            </span>
          ) : null}

          {effective.kind === 'followUp' && selectedFollowUp ? (
            <>
              {selectedFollowUp.status === 'open' ? (
                <LedgerBadge tone={dueTone[dueState(selectedFollowUp, now)]}>
                  {dueLabel(selectedFollowUp.dueAt, now)}
                </LedgerBadge>
              ) : (
                <LedgerBadge tone="slate">Resolved</LedgerBadge>
              )}
              <span className="min-w-0 flex-1 text-[13px] font-medium text-slate-900">
                {selectedFollowUp.title}
                {selectedFollowUp.assigneeName ? (
                  <span className="ml-2 text-[11px] font-normal text-slate-500">
                    → {selectedFollowUp.assigneeName}
                  </span>
                ) : null}
              </span>
              {selectedFollowUp.status === 'open' ? (
                <div className="flex items-center gap-1.5">
                  {onCompleteFollowUp ? (
                    <button
                      type="button"
                      className={answered ? toolbarButton : toolbarButtonDisabled}
                      disabled={!answered}
                      title={
                        answered ? undefined : 'Needs at least one note first'
                      }
                      onClick={() => setPendingAction('complete')}
                    >
                      Done
                    </button>
                  ) : null}
                  {onSnoozeFollowUp ? (
                    <button
                      type="button"
                      className={toolbarButton}
                      onClick={() => setPendingAction('snooze')}
                    >
                      Snooze
                    </button>
                  ) : null}
                </div>
              ) : onReopenFollowUp ? (
                <button
                  type="button"
                  className={toolbarButton}
                  onClick={() => setPendingAction('reopen')}
                >
                  Reopen
                </button>
              ) : null}
            </>
          ) : null}

        </div>

        {effective.kind === 'followUp' && selectedFollowUp?.details ? (
          <p className="text-[12px] text-slate-500">
            {selectedFollowUp.details}
          </p>
        ) : null}

        <CalloutNotesSection
          notes={mainFeed}
          feedLabel="Notes"
          newNoteAuthorName={newNoteAuthorName}
          showNewNoteArea={Boolean(onAddNote)}
          onAddNote={onAddNote ? (text) => onAddNote(text, target) : undefined}
          editingNoteId={editingNoteId}
          onStartEditNote={onSaveEditNote ? setEditingNoteId : undefined}
          onCancelEditNote={() => setEditingNoteId(null)}
          onSaveEditNote={
            onSaveEditNote
              ? (id, text) => {
                  onSaveEditNote(id, text, target)
                  setEditingNoteId(null)
                }
              : undefined
          }
          onDeleteNote={
            onDeleteNote
              ? (id) => {
                  onDeleteNote(id, target)
                  if (editingNoteId === id) setEditingNoteId(null)
                }
              : undefined
          }
          onSubmitPhotoNote={
            onSubmitPhotoNote
              ? (input) => onSubmitPhotoNote(input, target)
              : undefined
          }
          onReplacePhotoNote={
            onReplacePhotoNote
              ? (id, input) => onReplacePhotoNote(id, input, target)
              : undefined
          }
          photoError={photoNoteError}
          onClearPhotoError={onClearPhotoNoteError}
        />
      </div>

      {pendingAction && selectedFollowUp ? (
        <FollowUpActionDialog
          action={pendingAction}
          row={toActionRow(selectedFollowUp)}
          onCancel={() => setPendingAction(null)}
          onConfirm={(result) => {
            if (pendingAction === 'complete') {
              // Done with "don't remind again" ticked is the old Close.
              if (result.closeForGood) {
                onCloseFollowUp?.(selectedFollowUp.id, { note: result.note })
              } else {
                onCompleteFollowUp?.(selectedFollowUp.id, {
                  note: result.note,
                })
              }
            } else if (pendingAction === 'snooze' && result.step) {
              onSnoozeFollowUp?.(selectedFollowUp.id, result.step, {
                note: result.note,
              })
            } else if (pendingAction === 'snooze' && result.dueAt) {
              onRescheduleFollowUp?.(selectedFollowUp.id, result.dueAt, {
                note: result.note,
              })
            } else if (pendingAction === 'close') {
              onCloseFollowUp?.(selectedFollowUp.id, { note: result.note })
            } else if (pendingAction === 'reopen' && result.dueAt) {
              onReopenFollowUp?.(selectedFollowUp.id, result.dueAt, {
                note: result.note,
              })
            }
            setPendingAction(null)
          }}
        />
      ) : null}

    </div>
  )
}
